import React from "react";
import { FileUploader, Button } from "devextreme-react";
import xlsxParser from "xlsx-parse-json";
import { removeAccents } from "../../../../../helpers/strings";
import inputs, {
  validName,
  INPUT_ERRORS,
  onlyNumbers,
  addZeroToNum,
  validMovimientoPersonal,
  Input,
  INPUT_TYPES,
  MESES,
  isExcelFile,
} from "./inputs";
import validaciones from "../../../../../helpers/validaciones";
import WorkersTable from "./WorkersTable";
import FormButtons from "./FormButtons";
import { Tooltip } from "devextreme-react/ui/tooltip";
import { downloadFile } from "../utils";
import api from "../api";

import excel from "../files/ExcelTipo.xlsx";

let defaultRequiredExcelColumns = [
  "nombre",
  "apellido paterno",
  "apellido materno",
  "rut",
  "sexo",
  "remuneracion imponible",
  "movimiento personal",
];

const allColumns = [
  "Nombre",
  "Apellido paterno",
  "Apellido materno",
  "RUT",
  "Sexo",
  "Remuneración imponible",
  "Movimiento personal",
  "Fecha inicio movimiento",
  "Fecha término movimiento",
];

const parseWorkerKeys = (worker) => {
  for (let workerExcelColumn in worker) {
    // * Simplify the column name by removing accents and uppercases.
    // * This is so multiple names that mean the same work, for example:
    // * Column names: nombre, Nombre, NOMBRE
    // * Column names: Fecha término, fecha término, FECHA TÉRMINO

    const parsedWorkerExcelColumn = removeAccents(
      workerExcelColumn.toLowerCase()
    );
    if (parsedWorkerExcelColumn !== workerExcelColumn) {
      worker[parsedWorkerExcelColumn] = worker[workerExcelColumn];
      delete worker[workerExcelColumn];
    }
  }

  return worker;
};

const parseColumnData = (column, data) => {
  let parsedData = data;

  switch (column) {
    case "rut":
      parsedData = validaciones.formatRut(data, {
        showDots: true,
        showHyphen: true,
      });
      break;

    case "fecha inicio movimiento":
    case "fecha termino movimiento":
      if (data.includes("/")) {
        const aux = data.split("/");
        const par = `${
          +aux[0] > 12 ? aux[0] + "/" + aux[1] : aux[1] + "/" + aux[0]
        }/${aux[2].length === 4 ? "" : "20"}${aux[2]}`;

        parsedData = par.split("/");

        parsedData.forEach((part, index) => {
          parsedData[index] = addZeroToNum(+part);
        });

        parsedData = parsedData.join("-");
      }
      break;

    default:
      break;
  }

  return parsedData;
};

const validateColumnData = (worker, month, year, column, data) => {
  let error = false;
  const errorMssg = `El valor "${data}" de la columna ${column.toUpperCase()} del trabajador ${
    worker.nombre ? worker.nombre.toUpperCase() : ""
  } ${
    worker["apellido paterno"] ? worker["apellido paterno"].toUpperCase() : ""
  } ${
    worker["apellido materno"] ? worker["apellido materno"].toUpperCase() : ""
  } es incorrecto.`;

  switch (column) {
    case "nombre":
    case "apellido paterno":
    case "apellido materno":
      if (!validName(data)) {
        error = `${errorMssg} ${INPUT_ERRORS.personName}`;
      }
      break;

    case "rut":
      if (!validaciones.validateRut(data)) {
        error = `${errorMssg} ${INPUT_ERRORS.rut}`;
      }
      break;

    case "sexo":
      const sex = data.toLocaleLowerCase();
      if (sex !== "m" && sex !== "f") {
        error = `${errorMssg} ${INPUT_ERRORS.sex}`;
      }
      break;

    case "remuneracion imponible":
      if (!onlyNumbers(data)) {
        error = `${errorMssg} ${INPUT_ERRORS.number}`;
      }
      if (data >= 99999999) {
        error = `${errorMssg} El monto de remuneración excede el limite de $99.999.999 mensual`;
      }
      break;

    case "fecha inicio movimiento":
    case "fecha termino movimiento":
      const dataMonthNum = data.split("-")[1];
      const dataYearNum = data.split("-")[2];
      const dataDayNum = data.split("-")[0];
      if (month !== MESES[+dataMonthNum - 1]) {
        error = `${errorMssg} El mes del excel no coincide con el mes de cotización`;
      }
      if (year !== +dataYearNum) {
        error = `${errorMssg} El año del excel no coincide con el año de cotización`;
      }
      if (+dataDayNum > 31 || +dataDayNum < 1) {
        error = `${errorMssg} El día del excel no corresponde a un día valido`;
      }

      break;

    case "movimiento personal":
      if (!validMovimientoPersonal(data)) {
        error = `${errorMssg} ${INPUT_ERRORS.movimientoPersonal}`;
      }
      break;

    default:
      break;
  }

  return error;
};

const DownloadButton = () => {};

class Step2 extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      workers: [],
      error: "",
      month: props.steps.step1.mesRemuneracion,
      uploadedFile: [],
      cotizacionAPagar: "0",
      tasaCotizacion: "1",
      showDownloadTooltip: false,
      reajustes: "0",
      interesesPenales: "0",
      multas: "0",
    };
  }

  componentDidMount = () => {
    const {
      steps: { step2 },
    } = this.props;

    if (step2.workers) {
      this.setState({ ...step2 });
    }
  };

  handleStepUpdate = ({ toPreviousStep }) => {
    const { updateStepValues } = this.props;
    const { error, ...step2Data } = this.state;
    updateStepValues("step2", { ...step2Data }, toPreviousStep);
  };

  handleFileUpload = async ({ value }) => {
    const {
      steps: { step1 },
      companyId,
    } = this.props;
    const file = value[0];
    if (!file) return;
    if (this.state.uploadedFile[0] === value[0]) return;

    try {
      if (!isExcelFile(file)) {
        throw new Error(INPUT_ERRORS.invalidFileType);
      }
      const data = await xlsxParser.onFileSelection(file);
      let workers = [];

      for (let sheet in data) {
        const workersInsheet = data[sheet];
        if (workersInsheet && workersInsheet.length === 0) return;
        // * Verify that all workers data is properly formatted and valid
        workersInsheet.forEach((worker, workerIndex) => {
          worker = parseWorkerKeys(worker);
          // * Make these two columns required for this worker if
          // * his/her movimiento personal !== 0
          let requiredExcelColumns = [...defaultRequiredExcelColumns];
          if (+worker["movimiento personal"] !== 0) {
            requiredExcelColumns = [
              ...defaultRequiredExcelColumns,
              "fecha inicio movimiento",
              "fecha termino movimiento",
            ];
          }

          const missingExcelColumns = [...requiredExcelColumns];

          Object.keys(worker).forEach((workerExcelColumn, workerObjIndex) => {
            let workerColumnData = worker[workerExcelColumn];

            // * Parse data from excel value. This is mostly for date formats.
            workerColumnData = parseColumnData(
              workerExcelColumn,
              workerColumnData
            );
            workersInsheet[workerIndex][workerExcelColumn] = workerColumnData;

            // * Verify that all the requiredExcelColumns are there.
            // * If missingExcelColumns is empty, then the uploaded excel
            // * will have all the required columns. This is to get the missing ones.
            if (requiredExcelColumns.includes(workerExcelColumn)) {
              missingExcelColumns.splice(
                missingExcelColumns.indexOf(workerExcelColumn),
                1
              );
            }

            // * If on the last iteration of the worker there are missing columns still,
            // * then throw an error.
            const onWorkerObjLastIteration =
              workerObjIndex === Object.keys(worker).length - 1;
            if (onWorkerObjLastIteration && missingExcelColumns.length) {
              let columnsText = missingExcelColumns
                .map((x) => " " + x)
                .toString()
                .substring(1)
                .replace("remuneracion", "remuneración")
                .replace("termino", "término");
              throw new Error(
                `Data del trabajador ${
                  worker.nombre ? worker.nombre.toUpperCase() : ""
                }
                 ${
                   worker["apellido paterno"]
                     ? worker["apellido paterno"].toUpperCase()
                     : ""
                 }
                 ${
                   worker["apellido materno"]
                     ? worker["apellido materno"].toUpperCase()
                     : ""
                 } incompleta, faltan las columnas: ${columnsText}`
              );
            }

            // * Verify that each cell value if correct
            const error = validateColumnData(
              worker,
              this.props.steps.step1.mesRemuneracion,
              this.props.steps.step1.anoRemuneracion,
              workerExcelColumn,
              workerColumnData
            );

            if (error) throw new Error(`${error}`);
          });
        });

        workers = [...workers, ...workersInsheet];
      }

      // * Calculate total quotation
      let cotizacionAPagar = 0;
      workers.forEach((worker) => {
        cotizacionAPagar += +worker["remuneracion imponible"];
      });

      cotizacionAPagar = (cotizacionAPagar * (+step1.tasa * 0.01 || 1)).toFixed(
        2
      );

      let dat = {
        IV_RUT: companyId.toString(),
        IV_MES_REMU: (MESES.indexOf(step1.mesRemuneracion) + 1).toString(),
        IV_ANO_REMU: step1.anoRemuneracion.toString(),
        IV_CANT_TRABAJADORES: workers.length.toString(),
        IV_COTIZA_A_PAGAR: parseInt(`${cotizacionAPagar}`).toString(),
      };
      const RIM = await api.getRIM(dat);

      let reajustesValue =
        RIM && RIM.EV_MONTO_REAJUSTE && RIM.EV_MONTO_REAJUSTE !== ""
          ? RIM.EV_MONTO_REAJUSTE.toString()
          : "0";
      let interesesValue =
        RIM && RIM.EV_MONTO_INTERES && RIM.EV_MONTO_INTERES !== ""
          ? RIM.EV_MONTO_INTERES.toString()
          : "0";
      let multasValue =
        RIM && RIM.EV_MONTO_MULTA && RIM.EV_MONTO_MULTA !== ""
          ? RIM.EV_MONTO_MULTA.toString()
          : "0";

      this.setState({
        workers,
        uploadedFile: [file],
        error: "",
        cotizacionAPagar: parseInt(`${cotizacionAPagar}`).toString(),
        tasaCotizacion: `${step1.tasa || 1}`,
        reajustes: reajustesValue,
        interesesPenales: interesesValue,
        multas: multasValue,

        totalAPagar: (
          parseInt(`${cotizacionAPagar}`) +
          parseInt(`${reajustesValue}`) +
          parseInt(`${interesesValue}`) +
          parseInt(`${multasValue}`)
        ).toString(),
      });
    } catch (error) {
      console.error("error: ", error);
      this.setState({ error: error.toString(), uploadedFile: [] });
    }
  };

  handleMonthChange = (month) => {
    this.setState({
      month,
      workers: [],
      error: "",
      uploadedFile: [],
      cotizacionAPagar: "0",
      tasaCotizacion: "1",
      totalAPagar: "0",
    });
  };

  handleChange = (value, input) => {
    const newState = { ...this.state, [input]: value };
    const {
      cotizacionAPagar,
      tasaCotizacion,
      reajustes,
      interesesPenales,
      multas,
    } = newState;

    newState.totalAPagar = `${
      +cotizacionAPagar +
      +(reajustes || 0) +
      +(interesesPenales || 0) +
      +(multas || 0)
    }`;

    this.setState(newState);
  };

  toggleTooltip = () => {
    this.setState({ showDownloadTooltip: !this.state.showDownloadTooltip });
  };

  render() {
    const {
      workers,
      month,
      error,
      uploadedFile,
      showDownloadTooltip,
    } = this.state;
    const { steps } = this.props;
    return (
      <div id="cotizaciones-step2">
        <div>
          <span className="form-title">Carga de trabajadores</span>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{
                paddingTop: "1rem",
                paddingBottom: "1rem",
                textAlign: "justify",
              }}
            >
              Descarga la planilla Excel tipo, y una vez que esté completa,
              carga la planilla en el botón Cargar Excel.
            </span>
          </div>

          <div style={{ width: "51%" }}>
            <Input
              input={{
                inputType: INPUT_TYPES.TEXT,
                name: "month",
                label: "Mes a cotizar",
                disabled: true,
              }}
              value={steps.step1.mesRemuneracion}
            />
          </div>
          <br />

          <div style={{ display: "flex", alignItems: "center", width: "51%" }}>
            <Button
              id={"btnDescargarDoc"}
              height={40}
              className="dx-fileuploader-button dx-button dx-button-normal dx-button-mode-contained dx-widget dx-button-has-text"
              visible={true}
              onClick={() =>
                downloadFile({ fileUrl: excel, textFileName: "ExcelTipo.xlsx" })
              }
              style={{ color: "#00B2A9" }}
            >
              <i
                onMouseEnter={this.toggleTooltip}
                onMouseLeave={this.toggleTooltip}
                className="achs-icon search-bar-icon"
              >
                z
              </i>
              Descargar plantilla tipo
            </Button>
            <FileUploader
              value={uploadedFile}
              multiple={false}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              uploadMode="useForm"
              onValueChanged={this.handleFileUpload}
              uploadButtonText=""
              labelText=""
              selectButtonText="Carga planilla llena"
            />

            {showDownloadTooltip && (
              <Tooltip
                visible={showDownloadTooltip}
                closeOnOutsideClick
                contentRender={() => <div>Descargar Excel tipo</div>}
                position="top"
                className="header-filter-button"
                target="#btnDescargarDoc"
              />
            )}
          </div>

          <span style={{ paddingTop: "1rem" }} className="form-subtitle">
            {" "}
            Trabajadores ingresados: {workers.length}
          </span>

          {error && <p className="error-alert">{error}</p>}

          <WorkersTable workers={workers} />
          <div style={{ width: "51%" }}>
            {inputs.step2.map((input) => {
              const inputProps = {
                key: input.name,
                input,
                handleChange: this.handleChange,
                value: this.state[input.name],
              };

              return <Input {...inputProps} style={{ width: "50%" }} />;
            })}
          </div>
        </div>

        <FormButtons
          handleNext={this.handleStepUpdate}
          handleBack={() => this.handleStepUpdate({ toPreviousStep: true })}
          disableNext={!workers.length}
        />
      </div>
    );
  }
}

export default Step2;
